import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import CountdownTimer from '../components/CountdownTimer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  MapPin, Users, Gift, Clock, CheckCircle, AlertCircle, 
  ArrowLeft, Shield, Star, Building, ChevronRight 
} from 'lucide-react';
import { toast } from 'sonner';
import { TurnstileWidget, turnstileEnabled } from '../components/TurnstileWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GiveawayDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  
  const [giveaway, setGiveaway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    province_state: '',
    postal_code: '',
    property_type: '',
    ownership_status: '',
    timeline: '',
    skill_answer: '',
    consent_terms: false,
    consent_sponsor_contact: false,
    referral_code: ''
  });

  const fetchGiveaway = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/giveaways/${id}`);
      setGiveaway(response.data);
    } catch (error) {
      console.error('Error fetching giveaway:', error);
      navigate('/giveaways');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchGiveaway();
    // Check for referral code in URL
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code: refCode }));
      // Track referral click
      axios.post(`${API}/referrals/track/${refCode}`).catch(() => {});
    }
  }, [fetchGiveaway]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/giveaways/${id}` } });
      return;
    }

    if (!formData.consent_terms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    setSubmitting(true);

    try {
      // First save the profile
      await axios.post(`${API}/profile/homeowner`, {
        address: formData.address,
        city: formData.city,
        province_state: formData.province_state,
        postal_code: formData.postal_code,
        property_type: formData.property_type,
        ownership_status: formData.ownership_status,
        timeline: formData.timeline
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Then submit entry
      const fullAddress = `${formData.address}, ${formData.city}, ${formData.province_state} ${formData.postal_code}`;
      await axios.post(`${API}/entries`, {
        giveaway_id: id,
        address: fullAddress,
        skill_answer: formData.skill_answer,
        consent_terms: formData.consent_terms,
        consent_sponsor_contact: formData.consent_sponsor_contact,
        referral_code: formData.referral_code || null,
        turnstile_token: turnstileToken
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubmitted(true);
      toast.success('Entry submitted successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit entry. Please try again.');
      toast.error(err.response?.data?.detail || 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!giveaway) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="pt-32 text-center">
          <p className="text-neutral-600">Giveaway not found</p>
        </div>
      </div>
    );
  }

  const formatValue = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      
      {/* Back Button */}
      <div className="pt-24 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-4">
          <Link to="/giveaways" className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900">
            <ArrowLeft className="w-4 h-4" />
            Back to Giveaways
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img
          src={giveaway.image_url || 'https://images.unsplash.com/photo-1772567732969-c1506edf80a0'}
          alt={giveaway.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="bg-green-500 text-white font-bold px-4 py-2 rounded-full text-sm uppercase">
                Free Entry
              </span>
              <span className="bg-black/60 backdrop-blur-sm text-white font-bold px-4 py-2 rounded-full text-sm">
                {formatValue(giveaway.value)} Value
              </span>
              {giveaway.featured && (
                <span className="bg-[#FF3B30] text-white font-bold px-4 py-2 rounded-full text-sm uppercase">
                  Featured
                </span>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              {giveaway.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                <span>{giveaway.city}, {giveaway.province_state}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span>{giveaway.total_entries} entries</span>
              </div>
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5" />
                <span>{giveaway.sponsors?.length || 0} sponsors</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Countdown */}
              <div className="bg-neutral-900 rounded-2xl p-6">
                <div className="flex items-center gap-2 text-white/80 mb-4">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">Giveaway Ends In:</span>
                </div>
                <CountdownTimer endDate={giveaway.end_date} />
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl p-6 border border-neutral-200">
                <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>About This Giveaway</h2>
                <p className="text-neutral-600 leading-relaxed">{giveaway.description}</p>
                
                <div className="mt-6 pt-6 border-t border-neutral-100">
                  <h3 className="font-semibold mb-3">Project Type</h3>
                  <span className="bg-neutral-100 text-neutral-700 px-4 py-2 rounded-full text-sm font-medium capitalize">
                    {giveaway.project_type?.replace(/_/g, ' ')}
                  </span>
                </div>

                {giveaway.contractor_name && (
                  <div className="mt-6 pt-6 border-t border-neutral-100">
                    <h3 className="font-semibold mb-3">Assigned Contractor</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
                        <Building className="w-6 h-6 text-neutral-500" />
                      </div>
                      <div>
                        <p className="font-medium">{giveaway.contractor_name}</p>
                        <p className="text-sm text-neutral-500">Verified Contractor</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sponsors */}
              {giveaway.sponsor_details?.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border border-neutral-200">
                  <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>
                    Sponsored by {giveaway.sponsor_details.length} Top Realtors
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {giveaway.sponsor_details.map((sponsor) => (
                      <div key={sponsor.user_id || sponsor.company_name} className="flex items-center gap-3 p-4 bg-neutral-50 rounded-xl">
                        <img
                          src={sponsor.profile_image || 'https://images.pexels.com/photos/7578976/pexels-photo-7578976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                          alt={sponsor.company_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-medium">{sponsor.company_name}</p>
                          <p className="text-sm text-neutral-500 capitalize">{sponsor.business_type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Legal */}
              <div className="bg-neutral-100 rounded-2xl p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#FF3B30]" />
                  Legal Compliance
                </h3>
                <ul className="space-y-2 text-sm text-neutral-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    No purchase necessary to enter or win
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    Skill-testing question required (Canadian compliance)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    One entry per household
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    Winner notified via email
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column - Entry Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-2xl p-8 border border-neutral-200 text-center"
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Entry Submitted!</h3>
                    <p className="text-neutral-600 mb-6">
                      Good luck! We'll notify you if you're selected as the winner.
                    </p>
                    <Link to="/dashboard/entries">
                      <Button className="w-full bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">
                        View My Entries
                      </Button>
                    </Link>
                  </motion.div>
                ) : (
                  <div className="bg-white rounded-2xl p-6 border border-neutral-200">
                    <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Outfit' }}>Enter to Win</h2>
                    
                    {error && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <p className="text-red-700 text-sm">{error}</p>
                      </div>
                    )}

                    {!isAuthenticated ? (
                      <div className="text-center py-8">
                        <p className="text-neutral-600 mb-4">Please sign in to enter this giveaway</p>
                        <Link to="/login" state={{ from: `/giveaways/${id}` }}>
                          <Button className="w-full bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl" data-testid="login-to-enter">
                            Sign In to Enter
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4" data-testid="entry-form">
                        <div>
                          <Label>Street Address</Label>
                          <Input
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder="123 Main St"
                            className="mt-1 rounded-xl"
                            required
                            data-testid="address-input"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>City</Label>
                            <Input
                              value={formData.city}
                              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                              placeholder="Toronto"
                              className="mt-1 rounded-xl"
                              required
                              data-testid="city-input"
                            />
                          </div>
                          <div>
                            <Label>Province/State</Label>
                            <Select value={formData.province_state} onValueChange={(v) => setFormData({ ...formData, province_state: v })}>
                              <SelectTrigger className="mt-1 rounded-xl" data-testid="province-select">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Ontario">Ontario</SelectItem>
                                <SelectItem value="British Columbia">British Columbia</SelectItem>
                                <SelectItem value="Alberta">Alberta</SelectItem>
                                <SelectItem value="Quebec">Quebec</SelectItem>
                                <SelectItem value="Manitoba">Manitoba</SelectItem>
                                <SelectItem value="Saskatchewan">Saskatchewan</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label>Postal Code</Label>
                          <Input
                            value={formData.postal_code}
                            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                            placeholder="M5V 1A1"
                            className="mt-1 rounded-xl"
                            required
                            data-testid="postal-input"
                          />
                        </div>

                        <div>
                          <Label>Property Type</Label>
                          <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                            <SelectTrigger className="mt-1 rounded-xl" data-testid="property-type-select">
                              <SelectValue placeholder="Select property type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="house">House</SelectItem>
                              <SelectItem value="condo">Condo</SelectItem>
                              <SelectItem value="townhouse">Townhouse</SelectItem>
                              <SelectItem value="duplex">Duplex</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Ownership Status</Label>
                          <Select value={formData.ownership_status} onValueChange={(v) => setFormData({ ...formData, ownership_status: v })}>
                            <SelectTrigger className="mt-1 rounded-xl" data-testid="ownership-select">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owner">Owner</SelectItem>
                              <SelectItem value="renting">Renting</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>What are your plans?</Label>
                          <Select value={formData.timeline} onValueChange={(v) => setFormData({ ...formData, timeline: v })}>
                            <SelectTrigger className="mt-1 rounded-xl" data-testid="timeline-select">
                              <SelectValue placeholder="Select timeline" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="selling_soon">Selling soon (within 6 months)</SelectItem>
                              <SelectItem value="renovating_soon">Renovating soon</SelectItem>
                              <SelectItem value="browsing">Just browsing</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Skill Testing Question */}
                        <div className="p-4 bg-neutral-50 rounded-xl">
                          <Label className="text-neutral-900">Skill Testing Question (Required)</Label>
                          <p className="text-sm text-neutral-500 mt-1 mb-2">{giveaway.skill_testing_question}</p>
                          <Input
                            value={formData.skill_answer}
                            onChange={(e) => setFormData({ ...formData, skill_answer: e.target.value })}
                            placeholder="Your answer"
                            className="rounded-xl"
                            required
                            data-testid="skill-answer-input"
                          />
                        </div>

                        {/* Consent */}
                        <div className="space-y-3 pt-2">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="consent_terms"
                              checked={formData.consent_terms}
                              onCheckedChange={(c) => setFormData({ ...formData, consent_terms: c })}
                              data-testid="consent-terms-checkbox"
                            />
                            <label htmlFor="consent_terms" className="text-sm text-neutral-600 cursor-pointer">
                              I agree to the <a href="#" className="text-[#FF3B30] hover:underline">terms and conditions</a> and <a href="#" className="text-[#FF3B30] hover:underline">contest rules</a>
                            </label>
                          </div>
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="consent_sponsor"
                              checked={formData.consent_sponsor_contact}
                              onCheckedChange={(c) => setFormData({ ...formData, consent_sponsor_contact: c })}
                              data-testid="consent-sponsor-checkbox"
                            />
                            <label htmlFor="consent_sponsor" className="text-sm text-neutral-600 cursor-pointer">
                              I agree to be contacted by sponsors regarding real estate services
                            </label>
                          </div>
                        </div>

                        <TurnstileWidget onVerify={setTurnstileToken} />

                        <Button
                          type="submit"
                          disabled={submitting}
                          className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium"
                          data-testid="submit-entry-btn"
                        >
                          {submitting ? (
                            <span className="flex items-center gap-2">
                              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Submitting...
                            </span>
                          ) : (
                            'Submit Entry — It\'s Free'
                          )}
                        </Button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GiveawayDetail;

